import React from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { TransactionFormData } from "../../types/transaction.tsx";
import Input from "../ui/input.tsx";
import Button from "../ui/button.tsx";
import Label from "../ui/label.tsx";
import { transactionTypes } from "../../data/transactionTypes.tsx";

const transactionSchema = z.object({
  title: z.string().min(1, "Description is required"),
  type: z.string(),
  amount: z.number().positive("Amount must be greater than 0"),
  category: z.string(),
  date: z.string().min(1, "Date is required"),
  currency: z.string().min(1, "Currency is required"),
  notes: z.string().optional(),
});

interface AddTransactionFormProps {
  onSubmit: (data: TransactionFormData) => void;
  onError: (data: any) => void;
  defaultValues?: Partial<TransactionFormData>;
}

export function AddTransactionForm({
  onSubmit,
  onError,
  defaultValues,
}: AddTransactionFormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<TransactionFormData>({
    resolver: zodResolver(transactionSchema),
    defaultValues: {
      title: "",
      type: "expense",
      amount: 0,
      category: "miscellaneous",
      currency: "AED",
      date: new Date().toISOString().slice(0, 16),
      notes: "",
      ...(defaultValues || {}),
    },
  });

  const currentType = watch("type");

  // Currency selection is currently disabled; enable when needed.

  return (
    <form onSubmit={handleSubmit(onSubmit, onError)} className="space-y-6">
      {/* Type switch */}
      <div>
        <Label className="mb-1 block">Type</Label>
        <div className="inline-flex items-center gap-0.5 rounded-lg overflow-hidden border border-white/15 bg-white/5">
          {transactionTypes
            .filter((t) => t.id === "expense" || t.id === "income")
            .map((t) => (
              <button
                type="button"
                key={t.id}
                onClick={() =>
                  setValue("type", t.id as any, { shouldValidate: true })
                }
                className={[
                  "px-3 py-1 text-sm",
                  currentType === t.id
                    ? "bg-indigo-500/30 text-white"
                    : "text-white/80 hover:bg-white/10",
                ].join(" ")}
                aria-pressed={currentType === t.id}
              >
                {t.name}
              </button>
            ))}
        </div>
      </div>

      {/* Title */}
      <div>
        <Label htmlFor="title" className="mb-1 block">
          Description
        </Label>
        <Input
          id="title"
          type="text"
          placeholder="What's this for?"
          {...register("title")}
        />
        {errors.title && (
          <p className="text-sm text-red-500">{errors.title.message}</p>
        )}
      </div>

      {/* Amount and Currency */}
      <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
        <div>
          <Label htmlFor="amount" className="mb-1 block">
            Amount
          </Label>
          <Input
            id="amount"
            type="number"
            step="0.01"
            placeholder="Amount"
            {...register("amount", { valueAsNumber: true })}
          />
          {errors.amount && (
            <p className="text-sm text-red-500">{errors.amount.message}</p>
          )}
        </div>
      </div>

      <Button type="submit" className="w-full">
        Save Transaction
      </Button>
    </form>
  );
}
